import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import {
  MAX_COVER_BYTES,
  SERVICE_DISCOUNT_KIND_DEFS,
  SERVICE_EVENT_KIND_MAP,
  servicePrice,
  type ServiceDiscountKind,
  type ServiceEventKind,
} from '@goyone/shared';
import { dateTime, money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Combo } from '../../components/Combo';
import { RecordStats, type RecordStat } from '../../components/RecordStats';
import { CardPickerButton, useCardChoice } from '../../components/CardPicker';
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from '../../components/ColumnPicker';
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  CatViewSwitch,
  CoverField,
  EmptyState,
  ErrorState,
  FormTop,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
  useCatView,
} from '../../components/ui';

interface ServiceRow {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  category: string | null;
  department: string | null;
  unit: string;
  unitPrice: string;
  unitCost: string | null;
  taxable: boolean;
  isActive: boolean;
  discountKind: ServiceDiscountKind;
  discountValue: number | null;
  discountFrom: string | null;
  discountTo: string | null;
  effectivePrice: number;
  discountOff: number;
  discountActive: boolean;
  discountState: 'none' | 'active' | 'upcoming' | 'expired';
}

interface ServiceEvent {
  id: string;
  kind: ServiceEventKind;
  fromText: string | null;
  toText: string | null;
  note: string | null;
  actorName: string | null;
  createdAt: string;
}

interface ServicePage {
  items: ServiceRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** كتالوج أعمدة الخدمات. الاسم ثابتٌ: هو ما تُعرف به الخدمة. */
const SERVICE_COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: 'الاسم', fixed: true },
  { key: 'code', label: 'الكود' },
  { key: 'category', label: 'التصنيف' },
  { key: 'department', label: 'القسم' },
  { key: 'unit', label: 'الوحدة' },
  { key: 'unitPrice', label: 'السعر', group: 'الأرقام' },
  { key: 'discount', label: 'الخصم', group: 'الأرقام' },
  { key: 'isActive', label: 'الحالة' },
];

const SERVICE_NUMERIC = new Set(['unitPrice']);

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في services.service.ts. */
const SERVICE_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الاسم', kind: 'text', sortable: true },
  { key: 'code', label: 'الكود', kind: 'text' },
  { key: 'category', label: 'التصنيف', kind: 'text' },
  { key: 'department', label: 'القسم', kind: 'text' },
  { key: 'unit', label: 'الوحدة', kind: 'text' },
  { key: 'unitPrice', label: 'السعر', kind: 'number', sortable: true },
  {
    key: 'discount',
    label: 'الخصم',
    kind: 'enum',
    options: SERVICE_DISCOUNT_KIND_DEFS.map((d) => ({ value: d.key, label: d.label })),
  },
];

export function ServicesList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const columns = useMemo<ColumnDef[]>(() => [...SERVICE_COLUMNS], []);
  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice('goyone.columns.services', columns);
  const colDrag = useColumnDrag(columnState, setColumnState);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [editing, setEditing] = useState<ServiceRow | 'new' | null>(null);
  const [view, setView] = useCatView('goyone.view.services');

  const canEdit = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['services', query],
    queryFn: () => api<ServicePage>(`/sales/services${qs(query)}`),
  });

  const categories = useQuery({
    queryKey: ['services', 'categories'],
    queryFn: () => api<string[]>('/sales/services/categories'),
    staleTime: 5 * 60_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['services'] });
    setEditing(null);
  };

  const cardChoice = useCardChoice("goyone.stats.services", ['count', 'avgPrice', 'topPrice', 'inactive']);
  const statItems = list.data ? serviceCards(list.data) : [];
  const statChoices = statItems.map(({ key, label, group }) => ({ key, label, group }));

  return (
    <div className="page">
      <PageHead
        title="كتالوج الخدمات"
        subtitle={
          list.data
            ? `${plural(list.data.total, WORDS.service)} تُستخدم في عروض الأسعار والفواتير`
            : undefined
        }
      />

      {list.data && (
        <RecordStats
          items={statItems}
          storageKey="goyone.stats.services"
          defaults={['count', 'avgPrice', 'topPrice', 'inactive']}
          external={cardChoice}
        />
      )}

      {/* ترتيب الشريط كقوائم إدارة العملاء: بحثٌ فمرشّحات، والاختيارات والإضافة في طرفه. */}
      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الكود"
        />

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.isActive ?? ''}
          onChange={(e) => patch({ isActive: e.target.value || undefined })}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          <option value="true">النشطة</option>
          <option value="false">الموقوفة</option>
        </select>

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={SERVICE_FILTERS} filters={filters} onChange={setFilters} />
          <CatViewSwitch view={view} onView={setView} />
          {view === 'table' && (
            <ColumnPickerButton
              columns={columns}
              state={columnState}
              onChange={setColumnState}
              onReset={resetColumns}
              touched={columnsTouched}
            />
          )}
          <CardPickerButton
            choices={statChoices}
            chosen={cardChoice.chosen}
            onChange={cardChoice.setChosen}
            onReset={cardChoice.reset}
          />
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
              <Icon name="plus" size={14} />
              خدمة جديدة
            </button>
          )}
        </span>
      </div>

      <FilterChips defs={SERVICE_FILTERS} filters={filters} onChange={setFilters} />

      {/*
        التصنيفاتُ شريطٌ يُنقر لا قائمةٌ تُفتح.

        قائمةُ الاختيار تُخفي التصنيفاتِ خلف نقرةٍ ولا تقول كم فيها، فيبقى
        الكتالوجُ كتلةً واحدة. والشريطُ يعرضها كلَّها فيصير التصفّحُ
        بالتصنيف نقرةً واحدة.
      */}
      {(categories.data?.length ?? 0) > 0 && (
        <div className="cat-tabs" role="tablist" aria-label="التصنيفات">
          <button
            type="button"
            role="tab"
            aria-selected={!state.category}
            className={`cat-tab${state.category ? '' : ' is-on'}`}
            onClick={() => patch({ category: undefined })}
          >
            كل التصنيفات
          </button>
          {(categories.data ?? []).map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={state.category === c}
              className={`cat-tab${state.category === c ? ' is-on' : ''}`}
              onClick={() => patch({ category: c })}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {view === 'cards' ? (
        <>
          {list.isPending && <div className="skeleton" style={{ height: 220 }} />}

          {list.data && list.data.items.length > 0 && (
            <div className="cat-grid mb-4">
              {list.data.items.map((s) => (
                <ServiceCard
                  key={s.id}
                  service={s}
                  canEdit={canEdit}
                  onEdit={() => setEditing(s)}
                />
              ))}
            </div>
          )}

          {list.isError && (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          )}

          {list.data?.items.length === 0 && (
            <div className="card">
              <EmptyState
                title="لا خدمات هنا"
                text="جرّب تصنيفًا آخر، أو أضف خدمةً جديدة."
                action={
                  canEdit && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setEditing('new')}
                    >
                      خدمة جديدة
                    </button>
                  )
                }
              />
            </div>
          )}

          {list.data && list.data.pageCount > 1 && (
            <div className="card">
              <Pager
                page={list.data.page}
                pageCount={list.data.pageCount}
                total={list.data.total}
                pageSize={list.data.pageSize}
                onPage={(p) => patch({ page: String(p) })}
              />
            </div>
          )}
        </>
      ) : (
      <FilterScope
        defs={SERVICE_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/sales/services/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {shownColumns.map((c) => (
                  <th key={c.key} {...colDrag.thProps(c, SERVICE_NUMERIC.has(c.key) ? 'col-num' : undefined)}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                {canEdit && <th className="col-pin-end" />}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + (canEdit ? 1 : 0)} />}

            {list.data && (
              <tbody>
                {list.data.items.map((s) => (
                  <tr key={s.id}>
                    {shownColumns.map((c) => (
                      <ServiceCell key={c.key} column={c} service={s} />
                    ))}
                    {canEdit && (
                      <td className="end col-pin-end">
                        <button
                          className="btn btn-sm btn-icon btn-ghost"
                          onClick={() => setEditing(s)}
                          aria-label={`تعديل ${s.name}`}
                        >
                          <Icon name="edit" size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="الكتالوج فارغ"
            text="أضف خدماتك لتظهر جاهزة عند إعداد عروض الأسعار والفواتير."
            action={
              canEdit && (
                <button className="btn btn-primary" onClick={() => setEditing('new')}>
                  خدمة جديدة
                </button>
              )
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
          />
        )}
      </div>
      </FilterScope>
      )}

      {editing && (
        <ServiceModal
          service={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function ServiceModal({
  service,
  onClose,
  onSaved,
}: {
  service: ServiceRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    code: service?.code ?? '',
    name: service?.name ?? '',
    description: service?.description ?? '',
    category: service?.category ?? '',
    department: service?.department ?? '',
    unit: service?.unit ?? 'خدمة',
    imageUrl: service?.imageUrl ?? '',
    unitPrice: service ? String(Number(service.unitPrice)) : '',
    unitCost: service?.unitCost ? String(Number(service.unitCost)) : '',
    taxable: service?.taxable ?? true,
    isActive: service?.isActive ?? true,
    discountKind: (service?.discountKind ?? 'NONE') as ServiceDiscountKind,
    discountValue:
      service?.discountValue != null ? String(service.discountValue) : '',
    discountFrom: service?.discountFrom
      ? String(service.discountFrom).slice(0, 10)
      : '',
    discountTo: service?.discountTo ? String(service.discountTo).slice(0, 10) : '',
  });

  /* ما سبق من تصنيفاتٍ ووحداتٍ وأقسام — يُقترح ولا يُفرض. */
  const categories = useQuery({
    queryKey: ['services', 'categories'],
    queryFn: () => api<string[]>('/sales/services/categories'),
  });
  const units = useQuery({
    queryKey: ['services', 'units'],
    queryFn: () => api<string[]>('/sales/services/units'),
  });
  const departments = useQuery({
    queryKey: ['services', 'departments'],
    queryFn: () => api<string[]>('/sales/services/departments'),
  });

  const save = useMutation({
    mutationFn: () => {
      const none = form.discountKind === 'NONE';
      const body = {
        code: form.code || undefined,
        name: form.name,
        description: form.description || undefined,
        category: form.category || undefined,
        department: form.department || undefined,
        imageUrl: form.imageUrl || null,
        unit: form.unit,
        unitPrice: Number(form.unitPrice) || 0,
        unitCost: form.unitCost ? Number(form.unitCost) : undefined,
        taxable: form.taxable,
        isActive: form.isActive,
        discountKind: form.discountKind,
        discountValue: none ? null : Number(form.discountValue) || 0,
        discountFrom: none || !form.discountFrom ? null : form.discountFrom,
        discountTo: none || !form.discountTo ? null : form.discountTo,
      };
      return service
        ? api(`/sales/services/${service.id}`, { method: 'PATCH', body })
        : api('/sales/services', { method: 'POST', body });
    },
    onSuccess: onSaved,
  });

  /* السعرُ النافذُ يُحسب بالمعادلة نفسِها التي في الخادم — لا تقديرَ مختلف. */
  const preview = servicePrice({
    unitPrice: Number(form.unitPrice) || 0,
    discountKind: form.discountKind,
    discountValue: form.discountValue ? Number(form.discountValue) : null,
    discountFrom: form.discountFrom || null,
    discountTo: form.discountTo || null,
  });

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const margin =
    form.unitCost && Number(form.unitPrice) > 0
      ? Math.round(((Number(form.unitPrice) - Number(form.unitCost)) / Number(form.unitPrice)) * 100)
      : null;

  return (
    <Modal
      title={service ? `تعديل ${service.name}` : 'خدمة جديدة'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={form.name.trim().length < 2 || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {/*
        الغلافُ والحالةُ في الرأس، ثمّ النموذجُ أبوابًا لا سطورًا: هويّةٌ
        ثمّ تسعيرٌ ثمّ خصم. خمسةَ عشرَ حقلًا في شبكةٍ واحدةٍ تُقرأ حقلًا
        حقلًا ولا يُعرف أيُّها يخصّ ماذا.
      */}
      <FormTop
        image={form.imageUrl}
        onImage={(v) => set('imageUrl', v)}
        active={form.isActive}
        onActive={(v) => set('isActive', v)}
        maxBytes={MAX_COVER_BYTES}
      />

      <section className="fx-sec">
        <header className="fx-head">
          <span className="fx-ico">
            <Icon name="clipboard" size={15} />
          </span>
          <span>
            <b className="fx-title">تعريف الخدمة</b>
            <span className="fx-note">ما تُعرف به في الكتالوج والمستندات</span>
          </span>
        </header>

        <div className="form-grid">
          <div className="field span-2">
            <label className="label req" htmlFor="s-name">
              اسم الخدمة
            </label>
            <input
              id="s-name"
              className="input"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="مثال: حملة إعلانية مدفوعة"
              autoFocus
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="s-code">
              الكود
            </label>
            <input
              id="s-code"
              className="input"
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              dir="ltr"
              placeholder={service ? '' : 'يُولَّد تلقائيًّا'}
            />
            {!service && (
              <span className="tiny mute">
                اتركه فارغًا ليأخذ الرقم التالي — أو اكتب كودك.
              </span>
            )}
          </div>

          <div className="field">
            <label className="label" htmlFor="s-category">
              التصنيف
            </label>
            <Combo
              id="s-category"
              value={form.category}
              onChange={(v) => set('category', v)}
              options={categories.data ?? []}
              placeholder="اكتب أو اختر"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="s-department">
              القسم المنفِّذ
            </label>
            <Combo
              id="s-department"
              value={form.department}
              onChange={(v) => set('department', v)}
              options={departments.data ?? []}
              placeholder="اكتب أو اختر"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="s-unit">
              الوحدة
            </label>
            <Combo
              id="s-unit"
              value={form.unit}
              onChange={(v) => set('unit', v)}
              options={units.data ?? []}
              placeholder="اكتب أو اختر"
            />
          </div>

          <div className="field span-2">
            <label className="label" htmlFor="s-desc">
              الوصف
            </label>
            <textarea
              id="s-desc"
              className="input"
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="سطران يظهران في بطاقة الكتالوج وفي المستند."
            />
          </div>
        </div>
      </section>

      <section className="fx-sec" style={{ ['--fx-tone' as string]: 'var(--success)' }}>
        <header className="fx-head">
          <span className="fx-ico">
            <Icon name="wallet" size={15} />
          </span>
          <span>
            <b className="fx-title">التسعير</b>
            <span className="fx-note">سعرُ البيع، والتكلفةُ لحساب الهامش</span>
          </span>
        </header>

        <div className="form-grid">
          <div className="field">
            <label className="label" htmlFor="s-price">
              سعر البيع
            </label>
            <span className="in-suffix">
              <input
                id="s-price"
                className="input num"
                type="number"
                min="0"
                step="any"
                value={form.unitPrice}
                onChange={(e) => set('unitPrice', e.target.value)}
              />
              <i>ج.م</i>
            </span>
          </div>

          <div className="field">
            <label className="label" htmlFor="s-cost">
              التكلفة الداخلية
            </label>
            <span className="in-suffix">
              <input
                id="s-cost"
                className="input num"
                type="number"
                min="0"
                step="any"
                value={form.unitCost}
                onChange={(e) => set('unitCost', e.target.value)}
              />
              <i>ج.م</i>
            </span>
            {margin === null ? (
              <span className="tiny mute">
                اختيارية — تُستخدم لحساب هامش الربح.
              </span>
            ) : (
              <span className="marg">
                <span className="marg-bar">
                  <span style={{ width: `${Math.max(0, Math.min(100, margin))}%` }} />
                </span>
                <span className="marg-n num">هامش {margin}٪</span>
              </span>
            )}
          </div>

          <div className="field">
            <span className="label">الضريبة</span>
            <label className="sw">
              <input
                type="checkbox"
                checked={form.taxable}
                onChange={(e) => set('taxable', e.target.checked)}
              />
              <span className="sw-track">
                <span className="sw-dot" />
              </span>
              <span className="sw-text">
                <b>خاضعة للضريبة</b>
                <span className="tiny mute">تدخل في وعاء ضريبة المستند</span>
              </span>
            </label>
          </div>
        </div>
      </section>

      {/*
        الخصمُ ثلاثةُ خياراتٍ تُعرض ولا تُخبَّأ في قائمة، ومعه معاينةٌ
        تقول السعرَ النافذ اليوم — لا معادلةٌ يحلّها القارئ.
      */}
      <section className="fx-sec" style={{ ['--fx-tone' as string]: 'var(--warning)' }}>
        <header className="fx-head">
          <span className="fx-ico">
            <Icon name="target" size={15} />
          </span>
          <span>
            <b className="fx-title">الخصم</b>
            <span className="fx-note">بمدّته — يبدأ وينتهي وحده</span>
          </span>
          <span className="row">
            <span className="segr" role="radiogroup" aria-label="نوع الخصم">
              {SERVICE_DISCOUNT_KIND_DEFS.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  role="radio"
                  aria-checked={form.discountKind === d.key}
                  className={`segr-btn${form.discountKind === d.key ? ' is-on' : ''}`}
                  onClick={() => set('discountKind', d.key)}
                >
                  {d.label}
                </button>
              ))}
            </span>
          </span>
        </header>

        {form.discountKind === 'NONE' ? (
          <p className="tiny mute">
            لا خصم على هذه الخدمة — تُباع بسعرها المعلن.
          </p>
        ) : (
          <>
            <div className="form-grid">
              <div className="field">
                <label className="label req" htmlFor="s-dval">
                  {form.discountKind === 'PERCENT' ? 'نسبة الخصم' : 'مبلغ الخصم'}
                </label>
                <span className="in-suffix">
                  <input
                    id="s-dval"
                    className="input num"
                    type="number"
                    min="0"
                    step="any"
                    value={form.discountValue}
                    onChange={(e) => set('discountValue', e.target.value)}
                  />
                  <i>{form.discountKind === 'PERCENT' ? '٪' : 'ج.م'}</i>
                </span>
              </div>

              <div className="field">
                <label className="label" htmlFor="s-dfrom">
                  يبدأ في
                </label>
                <input
                  id="s-dfrom"
                  className="input num"
                  type="date"
                  value={form.discountFrom}
                  onChange={(e) => set('discountFrom', e.target.value)}
                />
              </div>

              <div className="field">
                <label className="label" htmlFor="s-dto">
                  ينتهي في
                </label>
                <input
                  id="s-dto"
                  className="input num"
                  type="date"
                  value={form.discountTo}
                  onChange={(e) => set('discountTo', e.target.value)}
                />
                <span className="tiny mute">
                  اليومان داخلان في المدّة. اتركهما فارغين لخصمٍ بلا أجل.
                </span>
              </div>
            </div>

            {Number(form.unitPrice) > 0 && (
              <div className={`disc-view${preview.active ? '' : ' is-idle'}`}>
                <b className="num disc-now">{money(preview.price)}</b>
                {preview.active && (
                  <span className="num disc-was">{money(Number(form.unitPrice))}</span>
                )}
                <span className="disc-say">
                  {preview.active
                    ? `السعر النافذ اليوم — بعد حسم ${money(preview.off)}`
                    : preview.state === 'upcoming'
                      ? 'الخصم لم يبدأ بعد — السعر المعروض هو سعر البيع.'
                      : preview.state === 'expired'
                        ? 'انتهت مدّة الخصم — السعر المعروض هو سعر البيع.'
                        : 'اكتب قيمة الخصم ليُحسب.'}
                </span>
              </div>
            )}
          </>
        )}
      </section>

      {service && <ServiceHistory serviceId={service.id} />}
    </Modal>
  );
}

/**
 * سجلُّ الخدمة — ما جرى عليها ومتى وبيد من.
 *
 * السعرُ يتغيّر والخصمُ يُوضع ويُرفع، ثمّ يُسأل بعد شهرين: «متى صار
 * كذا؟» — ولا جواب. وهنا الجواب، أحدثُه أوّلًا.
 */
function ServiceHistory({ serviceId }: { serviceId: string }) {
  const q = useQuery({
    queryKey: ['services', serviceId, 'history'],
    queryFn: () => api<ServiceEvent[]>(`/sales/services/${serviceId}/history`),
  });
  const rows = q.data ?? [];

  return (
    <>
      <h3 className="label mt-4 mb-2">سجلّ التغييرات</h3>
      {rows.length === 0 ? (
        <p className="tiny mute">لا تغييرات مسجَّلة بعد.</p>
      ) : (
        <div
          className="table-wrap rows-cap"
          style={{ ['--rows' as string]: 5, ['--rowh' as string]: '44px' }}
        >
          <table className="table">
            <thead>
              <tr>
                <th>ما جرى</th>
                <th>من</th>
                <th>إلى</th>
                <th>بيد</th>
                <th>متى</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Badge kind={SERVICE_EVENT_KIND_MAP[e.kind].kind}>
                      {SERVICE_EVENT_KIND_MAP[e.kind].label}
                    </Badge>
                  </td>
                  <td className="cell-mute">{e.fromText ?? '—'}</td>
                  <td className="cell-strong">{e.toText ?? '—'}</td>
                  <td className="cell-mute">{e.actorName ?? '—'}</td>
                  <td className="num cell-mute">{dateTime(e.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * بطاقةُ خدمة — الاسمُ والسعرُ والحالُ في لمحة.
 *
 * والسعرُ هو النافذ: إن كان عليها خصمٌ سارٍ ظهر بعد الحسم ومعه سعرُه
 * الأصليُّ مشطوبًا، فلا يُظنّ الخصمُ زيادة. وإن كُتب الخصمُ ولم يحن أوانُه
 * أو مضى، قيل ذلك في شارته ولم يُحسم من السعر.
 */
function ServiceCard({
  service,
  canEdit,
  onEdit,
}: {
  service: ServiceRow;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const off = service.discountKind !== 'NONE';
  const live = service.discountActive;

  return (
    <article className={`cat-card${service.isActive ? '' : ' is-off'}`}>
      <div className="cat-cover">
        {service.imageUrl ? (
          <img src={service.imageUrl} alt="" loading="lazy" />
        ) : (
          <span className="cat-cover-none">{service.name.slice(0, 1)}</span>
        )}
      </div>

      {off && (
        <span className={`cat-off${live ? '' : ' is-idle'}`}>
          {live
            ? service.discountKind === 'PERCENT'
              ? `خصم ${service.discountValue}٪`
              : `خصم ${money(service.discountOff)}`
            : service.discountState === 'upcoming'
              ? 'خصمٌ قادم'
              : 'خصمٌ منتهٍ'}
        </span>
      )}

      <div className="cat-top">
        {service.category ? (
          <span className="cat-chip">{service.category}</span>
        ) : (
          <span className="cat-code">بلا تصنيف</span>
        )}
        {service.code && (
          <span className="cat-code num" dir="ltr">
            {service.code}
          </span>
        )}
        {canEdit && (
          <span className="cat-act">
            <button
              className="btn btn-sm btn-icon btn-ghost"
              onClick={onEdit}
              aria-label={`تعديل ${service.name}`}
            >
              <Icon name="edit" size={14} />
            </button>
          </span>
        )}
      </div>

      <h3 className="cat-name">{service.name}</h3>
      {service.description && <p className="cat-desc">{service.description}</p>}

      <div className="cat-meta">
        {service.department && (
          <span>
            <Icon name="building" size={12} />
            {service.department}
          </span>
        )}
        <span>
          <Icon name="layers" size={12} />
          {service.unit}
        </span>
        {!service.taxable && (
          <span>
            <Icon name="receipt" size={12} />
            بلا ضريبة
          </span>
        )}
      </div>

      <div className="cat-foot">
        <span className="cat-price">
          <b className="num">{money(service.effectivePrice)}</b>
          {live && (
            <small className="num cat-was">{money(service.unitPrice)}</small>
          )}
        </span>
        <span className="cat-tail">
          <Badge kind={service.isActive ? 'success' : 'neutral'}>
            {service.isActive ? 'متاحة' : 'موقوفة'}
          </Badge>
        </span>
      </div>
    </article>
  );
}

/** خليّةٌ واحدة، يُختار شكلها بمفتاح العمود لا بموضعه. */
function ServiceCell({ column, service }: { column: ColumnDef; service: ServiceRow }) {
  switch (column.key) {
    case 'department':
      return <td className="cell-mute">{service.department ?? '—'}</td>;
    case 'discount':
      /*
       * الخصمُ يُقرأ بحاله لا بقيمته وحدها.
       *
       * «٢٥٪» مكتوبةً لا تقول أنافذةٌ هي أم انتهت مدّتها، فيُظنّ السعرُ
       * منخفضًا وهو لم ينخفض. فتُقال الحال، ويُذكر السعرُ النافذ معها.
       */
      if (service.discountKind === 'NONE') {
        return <td className="cell-mute">—</td>;
      }
      return (
        <td>
          {service.discountActive ? (
            <span className="row" style={{ gap: 6 }}>
              <Badge kind="success">
                {service.discountKind === 'PERCENT'
                  ? `${service.discountValue}٪-`
                  : `${money(service.discountOff)}-`}
              </Badge>
              <span className="num cell-strong">
                {money(service.effectivePrice)}
              </span>
            </span>
          ) : (
            <Badge kind="neutral">
              {service.discountState === 'upcoming' ? 'لم يبدأ' : 'انتهى'}
            </Badge>
          )}
        </td>
      );
    case 'name':
      return <td className="cell-strong">{service.name}</td>;
    case 'code':
      return <td className="num cell-mute">{service.code ?? '—'}</td>;
    case 'category':
      return <td className="cell-mute">{service.category ?? '—'}</td>;
    case 'unit':
      return <td className="cell-mute">{service.unit}</td>;
    case 'unitPrice':
      return <td className="num">{money(service.unitPrice)}</td>;
    case 'isActive':
      return (
        <td>
          <Badge kind={service.isActive ? 'success' : 'neutral'}>
            {service.isActive ? 'متاحة' : 'موقوفة'}
          </Badge>
        </td>
      );
    default:
      return <td>—</td>;
  }
}

/** بطاقات الكتالوج — الإجمالي من العدّاد، والباقي من هذه الصفحة بتصريح. */
function serviceCards(page: ServicePage): RecordStat[] {
  const rows = page.items;
  const prices = rows.map((r) => Number(r.unitPrice)).filter((v) => Number.isFinite(v));
  const avg = prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
  const top = prices.length ? Math.max(...prices) : 0;
  const inactive = rows.filter((r) => !r.isActive).length;
  const taxable = rows.filter((r) => r.taxable).length;
  return [
    { key: 'count', label: 'خدمات الكتالوج', group: 'الكتالوج', icon: 'inbox', color: '#3672EA', value: page.total, numeric: true },
    { key: 'avgPrice', label: 'متوسط سعر الوحدة', group: 'الأسعار', icon: 'receipt', color: '#51A521', value: money(avg), numeric: true, note: 'من هذه الصفحة' },
    { key: 'topPrice', label: 'أعلى سعر', group: 'الأسعار', icon: 'chart', color: '#1D46A6', value: money(top), numeric: true, note: 'من هذه الصفحة' },
    { key: 'taxable', label: 'خاضعة للضريبة', group: 'الكتالوج', icon: 'clipboard', color: '#BF930B', value: taxable, numeric: true, note: 'من هذه الصفحة' },
    { key: 'inactive', label: 'موقوفة', group: 'الكتالوج', icon: 'alert', color: '#DC2626', value: inactive, numeric: true, note: 'من هذه الصفحة' },
  ];
}
